// src/components/admin/tabs/VisualReferenceAdminTab.jsx
// 视觉参考库三栏工作台：用途分类侧栏 / 参考卡片墙 / 审核与生成。
// 删除入口进入管理员回收站；生成、保存与轮询仍沿用原业务流程。
import { appConfirm } from '../../../services/appDialogs';
import { AppSelect } from '../../ui/AppSelect';
import { DATASTART_IMAGE_MODELS, getCompatibleImageAspectOptions, normalizeDatastartImageAspectRatio } from '../../../constants/datastartImageModels';
import { ASTRAFLOW_IMAGE_MODELS } from '../../../constants/astraflowImageModels';
import { ASTRAFLOW_ASTRA_MODELS, isAstraCombinedProvider, normalizeAstraProviderId } from '../../../constants/astraflowAstraModels';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Eye, Images, Plus, RefreshCw, Save, Search, Sparkles, Trash2 } from "lucide-react";
import {
  fetchVisualReferences, createVisualReference, updateVisualReference, deleteVisualReference,
  generateVisualReference, syncVisualReference, seedDefaultVisualReferences,
} from "../../../services/adminService";
import { useSequentialPolling } from "../../../hooks/useSequentialPolling";
import {
  ADMIN_ACCENT, DangerButton, EmptyBlock, ErrorNotice, Panel, PrimaryButton, SecondaryButton,
} from "../adminUi.jsx";
import {
  getWanxCompatibleQualityOptions,
  normalizeWanxImageQualityTier,
} from "../../../utils/wanxImageCapabilities";

const ACCENT = ADMIN_ACCENT;

// ============================
// 视觉参考图库：画风 / 焦段 / 镜头参数
// ============================
const VISUAL_REFERENCE_CATEGORIES = [
  { value: 'reference_base', label: '基准图' },
  { value: 'visual_style', label: '画风' },
  { value: 'lens_focal', label: '焦段' },
  { value: 'shot_size', label: '景别' },
  { value: 'camera_move', label: '运镜' },
  { value: 'lighting', label: '打光' },
  { value: 'frame_rate', label: '帧率/速度' },
  { value: 'aperture', label: '光圈' },
];

const VISUAL_REFERENCE_PROVIDERS = [
  ...ASTRAFLOW_IMAGE_MODELS.map(({ provider, label }) => ({ value: provider, label })),
  ...DATASTART_IMAGE_MODELS.map(({ provider, label }) => ({ value: provider, label })),
  { value: 'HUOBAO_GPT_IMAGE_CHEAP', label: 'Image-2_线路2-4K · 推荐批量 4K 生图' },
  { value: 'WANGSUGPT', label: 'Image-2_线路1 · 推荐高质量画面' },
  { value: 'ASTRAFLOW_OPENAI', label: 'Image-2_线路3 · 推荐精细生图' },
  { value: 'ASTRAFLOW_OPENAI_LUNA', label: 'Image-2 + Luna_线路3 · 推荐轻量快速生成' },
  { value: 'ASTRAFLOW_OPENAI_TERRA', label: 'Image-2 + Terra_线路3 · 推荐均衡创作' },
  { value: 'ASTRAFLOW_OPENAI_SOL', label: 'Image-2 + Sol_线路3 · 推荐高难度创作' },
  ...ASTRAFLOW_ASTRA_MODELS.map(({ provider, label }) => ({ value: provider, label })),
  { value: 'ASTRAFLOW_GROK', label: 'Grok Imagine_线路3 · 推荐创意构图' },
  { value: 'WANGSUGEM', label: 'Gemini_线路1 · 推荐复杂画面' },
  { value: 'HUOBAO', label: 'Gemini_线路2 · 推荐日常快速生成' },
  { value: 'ASTRAFLOW_GEMINI', label: 'Gemini_线路3 · 推荐视觉理解' },
  { value: 'WANX', label: '千问图像3.0 Pro · 推荐商品图与局部修图' },
  { value: 'guavideo', label: 'Guavideo · 推荐动态运镜参考' },
  { value: 'engguavideo', label: 'EngGuaVideo · 推荐出海视频参考' },
];

const VISUAL_REFERENCE_IMAGE_QUALITY_OPTIONS = [
  { value: '1K', label: '1K' },
  { value: '2K', label: '2K' },
  { value: '4K', label: '4K' },
];

const emptyVisualReference = () => ({
  category: 'visual_style',
  itemKey: `ref_${Date.now()}`,
  name: '',
  nameEn: '',
  summary: '',
  prompt: '',
  negativePrompt: '文字、水印、字幕、logo、界面元素、低清晰度、画面畸变',
  usageScene: '',
  market: 'global',
  mediaType: 'image',
  aspectRatio: '16:9',
  provider: 'HUOBAO_GPT_IMAGE_CHEAP',
  imageQualityTier: '4K',
  referenceUrl: '',
  thumbnailUrl: '',
  previewVideoUrl: '',
  status: 'draft',
  sortOrder: 100,
  tags: '',
  metadata: '',
});

const EMPTY_VISUAL_REFERENCE_ITEMS = [];

const VISUAL_REFERENCE_PERF_TAG = '[DEBUG-VISREF-PERF]';

const visualReferencePerfEnabled = () => {
  try {
    return localStorage.getItem('visual_reference_perf') !== 'off';
  } catch {
    return true;
  }
};

const visualReferencePerfLog = (event, payload = {}) => {
  if (!visualReferencePerfEnabled()) return;
  console.info(VISUAL_REFERENCE_PERF_TAG, event, {
    at: Math.round(performance.now()),
    ...payload,
  });
};

const afterNextPaint = (callback) => {
  requestAnimationFrame(() => {
    requestAnimationFrame(callback);
  });
};

const summarizeVisualReferenceItems = (items = []) => ({
  total: items.length,
  active: items.filter(item => item.status === 'active').length,
  generating: items.filter(item => item.status === 'generating').length,
  withReferenceUrl: items.filter(item => item.referenceUrl).length,
  withThumbnailUrl: items.filter(item => item.thumbnailUrl).length,
  withPreviewVideoUrl: items.filter(item => item.previewVideoUrl).length,
  categories: items.reduce((acc, item) => {
    const key = item.category || 'unknown';
    acc[key] = (acc[key] || 0) + 1;
    return acc;
  }, {}),
});

const isLikelyFullSizeVisualReferenceImage = (item = {}) => (
  item.referenceUrl
  && (!item.thumbnailUrl || item.thumbnailUrl === item.referenceUrl)
);

const withCosThumbnail = (url, width = 640) => {
  if (!url || typeof url !== 'string') return '';
  if (!/\.cos\.[^/]+\.myqcloud\.com\//i.test(url)) return url;
  if (/imageMogr2|ci-process=/i.test(url)) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}imageMogr2/thumbnail/${width}x/format/webp`;
};

const getVisualReferenceCardImage = (item = {}) => {
  if (item.thumbnailUrl && item.thumbnailUrl !== item.referenceUrl) {
    return item.thumbnailUrl;
  }
  return withCosThumbnail(item.referenceUrl, 420);
};

const getVisualReferenceDetailImage = (item = {}) => {
  if (item.thumbnailUrl && item.thumbnailUrl !== item.referenceUrl) {
    return item.thumbnailUrl;
  }
  return withCosThumbnail(item.referenceUrl, 960);
};

const getVisualReferenceVideo = (item = {}) => item.previewVideoUrl || '';
const isVisualReferenceVideo = (item = {}) => (item.mediaType || '').toLowerCase() === 'video' || !!item.previewVideoUrl;

// ============================
// 表单字段（纯展示组件，挂在模块级避免每次渲染重建）
// 边框走 --vr-field-border 变量：focus-within 时由 Tailwind arbitrary property 切换为 hover 边框色，统一控件语言
// ============================
const FIELD_STYLE = {
  backgroundColor: 'var(--color-bg-tertiary)',
  border: '1px solid var(--vr-field-border, var(--color-border-primary))',
  color: 'var(--color-text-primary)',
};
const FIELD_WRAPPER_CLASS = 'flex flex-col gap-1.5 min-w-0 focus-within:[--vr-field-border:var(--color-border-hover)]';

const SelectField = ({ label, value, onChange, children }) => (
  <label className={FIELD_WRAPPER_CLASS}>
    <span className="text-label">{label}</span>
    <AppSelect
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      className="h-8 px-2 rounded-md text-xs outline-none transition-colors duration-150"
      style={FIELD_STYLE}
    >
      {children}
    </AppSelect>
  </label>
);

const InputField = ({ label, value, onChange, placeholder }) => (
  <label className={FIELD_WRAPPER_CLASS}>
    <span className="text-label">{label}</span>
    <input
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      placeholder={placeholder}
      className="h-8 px-2.5 rounded-md text-xs outline-none transition-colors duration-150"
      style={FIELD_STYLE}
    />
  </label>
);

const TextAreaField = ({ label, value, onChange, rows = 4, placeholder }) => (
  <label className={FIELD_WRAPPER_CLASS}>
    <span className="text-label">{label}</span>
    <textarea
      value={value || ''}
      onChange={(event) => onChange(event.target.value)}
      rows={rows}
      placeholder={placeholder}
      className="px-2.5 py-2 rounded-md text-xs outline-none resize-y transition-colors duration-150"
      style={FIELD_STYLE}
    />
  </label>
);

// ============================
// 参考卡片（memo：卡片墙渲染性能敏感，perf 日志依赖其稳定身份）
// 视觉：hover 微抬升 + 边框加深（--vr-card-border 变量切换）；选中态单层 accent 描边；
// 占位态降噪为小图标 + 单行 muted 小字；状态为小圆点 + 文字的 tint 徽章
// ============================
const VISUAL_REFERENCE_STATUS_COLORS = {
  active: 'var(--color-success)',
  generating: 'var(--color-warning)',
  failed: 'var(--color-error)',
};

const VisualReferenceCard = memo(function VisualReferenceCard({
  item,
  active,
  categoryLabelText,
  statusLabelText,
  marketLabelText,
  onSelect,
}) {
  const image = getVisualReferenceCardImage(item);
  const video = getVisualReferenceVideo(item);
  const videoItem = isVisualReferenceVideo(item);
  const statusColor = VISUAL_REFERENCE_STATUS_COLORS[item.status] || 'var(--color-text-muted)';
  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(item)}
      onKeyDown={(event) => {
        if (event.key === 'Enter') onSelect(item);
      }}
      className={`rounded-lg overflow-hidden cursor-pointer transition-[transform,border-color,background-color] duration-150 group hover:-translate-y-px ${active ? '' : 'hover:[--vr-card-border:var(--color-border-primary)]'}`}
      style={{
        backgroundColor: 'var(--color-bg-secondary)',
        border: `1px solid ${active ? 'var(--color-warning)' : 'var(--vr-card-border, var(--color-border-secondary))'}`,
        contentVisibility: 'auto',
        containIntrinsicSize: '260px 300px',
      }}
    >
      <div className="relative aspect-[4/3] overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
        {videoItem && video ? (
          <video
            src={video}
            className="w-full h-full object-cover"
            muted
            loop
            playsInline
            preload={active ? 'metadata' : 'none'}
            onMouseEnter={(event) => event.currentTarget.play().catch(() => {})}
            onMouseLeave={(event) => {
              event.currentTarget.pause();
              event.currentTarget.currentTime = 0;
            }}
          />
        ) : image ? (
          <img
            src={image}
            alt={item.name || '视觉参考图'}
            className="w-full h-full object-cover"
            loading={active ? 'eager' : 'lazy'}
            decoding="async"
            draggable={false}
          />
        ) : item.status === 'generating' ? (
          <div className="w-full h-full relative">
            <div className="skeleton absolute inset-0" style={{ borderRadius: 0 }} />
            <div className="absolute inset-0 flex items-center justify-center gap-1.5 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
              <Sparkles size={13} style={{ color: ACCENT }} />
              生成中
            </div>
          </div>
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center gap-1.5" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}>
            <Images size={16} style={{ color: 'var(--color-text-muted)', opacity: 0.55 }} />
            <span className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>等待生成</span>
          </div>
        )}
        <div className="absolute inset-x-0 top-0 p-2 flex items-start justify-between gap-2 pointer-events-none">
          <span className="text-2xs px-1.5 py-0.5 rounded-md font-medium" style={{ backgroundColor: 'var(--overlay-medium)', color: 'var(--color-text-primary)' }}>
            {categoryLabelText}
          </span>
          <span
            className="inline-flex items-center gap-1 text-2xs px-1.5 py-0.5 rounded-md font-medium"
            style={{
              backgroundColor: `color-mix(in srgb, ${statusColor} 16%, var(--overlay-medium))`,
              color: 'var(--color-text-primary)',
            }}
          >
            <span
              className={`w-1.5 h-1.5 rounded-full shrink-0 ${item.status === 'generating' ? 'animate-pulse' : ''}`}
              style={{ backgroundColor: statusColor }}
            />
            {statusLabelText}
          </span>
        </div>
      </div>
      <div className="p-3">
        <div className="flex items-start justify-between gap-2.5">
          <div className="min-w-0">
            <div className="font-medium text-[13px] leading-snug line-clamp-2 min-h-[36px]" style={{ color: 'var(--color-text-primary)' }}>{item.name || '未命名参考项'}</div>
            <div className="font-mono-num text-2xs mt-1 truncate" style={{ color: 'var(--color-text-muted)' }}>
              {item.itemKey} · {marketLabelText} · {videoItem ? '视频' : (item.imageQualityTier || '-')}
            </div>
          </div>
          {item.taskId && (
            <span className="text-2xs px-1.5 py-0.5 rounded-md shrink-0" style={{ backgroundColor: 'var(--color-bg-tertiary)', color: 'var(--color-text-muted)' }}>
              有任务
            </span>
          )}
        </div>
        <p className="text-xs line-clamp-2 mt-2 min-h-[36px] leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {item.summary || item.usageScene || item.prompt || '未填写说明'}
        </p>
      </div>
    </div>
  );
});

// ============================
// 侧栏筛选行（左侧 2px 琥珀指示条 + 激活 tint）
// ============================
const SideFilterRow = ({ active, label, count, onClick, compact = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left rounded-md transition-colors duration-150 cursor-pointer ${compact ? 'px-2.5 py-1.5' : 'px-2.5 py-2'} ${active ? '' : 'hover:bg-[var(--color-bg-tertiary)]'}`}
    style={active ? {
      backgroundColor: 'color-mix(in srgb, var(--color-warning) 10%, transparent)',
      boxShadow: 'inset 2px 0 0 var(--color-warning)',
    } : undefined}
  >
    <div className="flex items-center justify-between gap-2">
      <span className={`${compact ? 'text-xs' : 'text-[13px]'} font-medium truncate`} style={{ color: active ? 'var(--color-text-primary)' : 'var(--color-text-secondary)' }}>{label}</span>
      {count != null && (
        <span className="font-mono-num text-2xs shrink-0" style={{ color: active ? ACCENT : 'var(--color-text-muted)' }}>{count}</span>
      )}
    </div>
  </button>
);

const VisualReferenceAdminTab = () => {
  const [filters, setFilters] = useState({ q: '', category: '', status: '', mediaType: '', market: '' });
  const [data, setData] = useState(null);
  const [selectedId, setSelectedId] = useState('');
  const [draft, setDraft] = useState(emptyVisualReference());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const loadSeqRef = useRef(0);
  const pendingLoadPerfRef = useRef(null);

  const items = data?.items || EMPTY_VISUAL_REFERENCE_ITEMS;
  const selected = selectedId === 'new'
    ? null
    : items.find(item => String(item.id) === String(selectedId)) || items[0] || null;
  const activeId = selectedId || (selected ? String(selected.id) : '');

  // load 只依赖 filters 的五个字段；逐字段解构 + 同形重组，请求参数与 perf 日志内容与原实现一致
  const { q, category, status, mediaType, market } = filters;
  const load = useCallback(async ({ signal } = {}) => {
    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    const startedAt = performance.now();
    const currentFilters = { q, category, status, mediaType, market };
    visualReferencePerfLog('load:start', { seq, filters: currentFilters });
    setLoading(true);
    setError('');
    try {
      const res = await fetchVisualReferences(currentFilters, { signal });
      if (signal?.aborted) return;
      const fetchMs = Math.round(performance.now() - startedAt);
      const nextItems = res?.items || [];
      pendingLoadPerfRef.current = { seq, startedAt, fetchMs, itemCount: nextItems.length };
      visualReferencePerfLog('load:fetch-done', {
        seq,
        fetchMs,
        filteredTotal: res?.filteredTotal,
        total: res?.total,
        ...summarizeVisualReferenceItems(nextItems),
      });
      setData(res);
      afterNextPaint(() => {
        const pending = pendingLoadPerfRef.current;
        if (!pending || pending.seq !== seq) return;
        visualReferencePerfLog('load:paint-done', {
          seq,
          fetchMs: pending.fetchMs,
          totalMs: Math.round(performance.now() - startedAt),
          itemCount: pending.itemCount,
        });
      });
    } catch (e) {
      if (signal?.aborted) return;
      console.error(e);
      visualReferencePerfLog('load:error', {
        seq,
        ms: Math.round(performance.now() - startedAt),
        message: e?.message || String(e),
      });
      setError(e?.message || '视觉参考图库加载失败');
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [q, category, status, mediaType, market]);

  const hasGeneratingItems = items.some(item => item.status === 'generating');
  const refreshVisualReferences = useSequentialPolling(load, 6000, {
    enabled: hasGeneratingItems,
    immediate: false,
  });

  useEffect(() => {
    const timer = setTimeout(refreshVisualReferences, 200);
    return () => clearTimeout(timer);
  }, [load, refreshVisualReferences]);

  useEffect(() => {
    if (selectedId === 'new') return;
    if (!items.length) {
      if (selectedId) setSelectedId('');
      return;
    }
    const next = selected || items[0];
    if (!selectedId || !items.some(item => String(item.id) === String(selectedId))) {
      setSelectedId(String(next.id));
    }
    setDraft({ ...emptyVisualReference(), ...next });
  }, [items, selected, selectedId]);

  const setFilter = (key, value) => setFilters(prev => ({ ...prev, [key]: value }));
  const setField = (key, value) => setDraft(prev => ({ ...prev, [key]: value }));
  const effectiveImageProvider = isAstraCombinedProvider(draft.provider)
    ? normalizeAstraProviderId(draft.provider).toUpperCase()
    : draft.provider;
  const availableImageQualityOptions = getWanxCompatibleQualityOptions(
    VISUAL_REFERENCE_IMAGE_QUALITY_OPTIONS,
    effectiveImageProvider,
  );
  const categoryLabel = (value) => VISUAL_REFERENCE_CATEGORIES.find(item => item.value === value)?.label || value || '未分类';
  const statusLabel = (value) => ({ draft: '草稿', generating: '生成中', active: '可用', failed: '失败', canceled: '已取消' }[value] || value || '-');
  const marketLabel = (value) => ({ global: '通用', domestic: '国内', overseas: '出海' }[value] || value || '-');

  const handleSelectVisualReference = useCallback((item) => {
    const startedAt = performance.now();
    visualReferencePerfLog('select:start', {
      id: item?.id,
      category: item?.category,
      status: item?.status,
      hasReferenceUrl: !!item?.referenceUrl,
      hasThumbnailUrl: !!item?.thumbnailUrl,
      referenceUrlLength: item?.referenceUrl?.length || 0,
      thumbnailUrlLength: item?.thumbnailUrl?.length || 0,
      likelyFullSizeImage: isLikelyFullSizeVisualReferenceImage(item),
      promptLength: item?.prompt?.length || 0,
      metadataLength: item?.metadata?.length || 0,
      currentItemCount: items.length,
    });
    setSelectedId(String(item.id));
    setDraft({ ...emptyVisualReference(), ...item });
    afterNextPaint(() => {
      visualReferencePerfLog('select:paint-done', {
        id: item?.id,
        totalMs: Math.round(performance.now() - startedAt),
      });
    });
  }, [items.length]);

  const startNew = () => {
    setSelectedId('new');
    setDraft(emptyVisualReference());
    setNotice('');
    setError('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const normalizedDraft = {
        ...draft,
        provider: effectiveImageProvider,
        aspectRatio: normalizeDatastartImageAspectRatio(draft.aspectRatio, effectiveImageProvider),
        imageQualityTier: normalizeWanxImageQualityTier(draft.imageQualityTier, effectiveImageProvider),
      };
      const saved = normalizedDraft.id
        ? await updateVisualReference(normalizedDraft.id, normalizedDraft)
        : await createVisualReference(normalizedDraft);
      setSelectedId(String(saved.id));
      setDraft({ ...emptyVisualReference(), ...saved });
      setNotice('已保存');
      await refreshVisualReferences();
    } catch (e) {
      console.error(e);
      setError(e?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handleSeedDefaults = async () => {
    setSaving(true);
    setError('');
    setNotice('');
    try {
      const res = await seedDefaultVisualReferences();
      setNotice(`默认条目已初始化，新增 ${res?.created ?? 0} 条`);
      await refreshVisualReferences();
    } catch (e) {
      console.error(e);
      setError(e?.message || '初始化默认条目失败');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerate = async () => {
    if (!draft.id) {
      setError('请先保存条目，再生成参考图。');
      return;
    }
    if (!draft.prompt?.trim()) {
      setError('生成前需要填写提示词。');
      return;
    }
    setGenerating(true);
    setError('');
    setNotice('');
    try {
      const res = await generateVisualReference(draft.id, {
        prompt: draft.prompt,
        provider: effectiveImageProvider,
        aspectRatio: normalizeDatastartImageAspectRatio(draft.aspectRatio, effectiveImageProvider),
        imageQualityTier: normalizeWanxImageQualityTier(draft.imageQualityTier, effectiveImageProvider),
      });
      const item = res?.item || res;
      setDraft({ ...emptyVisualReference(), ...item });
      setSelectedId(String(item.id));
      setNotice(`已提交生成任务：${item.taskId || res?.task?.internalTaskId || ''}`);
      await refreshVisualReferences();
    } catch (e) {
      console.error(e);
      setError(e?.message || '提交生成失败');
    } finally {
      setGenerating(false);
    }
  };

  const handleSync = async () => {
    if (!draft.id) return;
    setSaving(true);
    setError('');
    try {
      const synced = await syncVisualReference(draft.id);
      setDraft({ ...emptyVisualReference(), ...synced });
      setNotice('任务状态已同步');
      await refreshVisualReferences();
    } catch (e) {
      console.error(e);
      setError(e?.message || '同步失败');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!draft.id) return;
    if (!(await appConfirm('确定将这个视觉参考条目移入回收站吗？移入后将不再显示，管理员可在回收站恢复。', { title: '移入回收站', confirmLabel: '移入回收站' }))) return;
    setSaving(true);
    setError('');
    try {
      await deleteVisualReference(draft.id);
      setNotice('已移入回收站，管理员可在回收站恢复。');
      setSelectedId('');
      setDraft(emptyVisualReference());
      await refreshVisualReferences();
    } catch (e) {
      console.error(e);
      setError(e?.message || '移入回收站失败');
    } finally {
      setSaving(false);
    }
  };

  const categoryCounts = useMemo(() => VISUAL_REFERENCE_CATEGORIES.map((cat) => ({
    ...cat,
    count: items.filter(item => item.category === cat.value).length,
  })), [items]);
  const filteredTotal = data?.filteredTotal;
  const statusSteps = useMemo(() => [
    { value: '', label: '全部', desc: '资料库总览', count: filteredTotal ?? items.length },
    { value: 'draft', label: '待定义', desc: '未发布或未生成', count: items.filter(item => item.status === 'draft').length },
    { value: 'generating', label: '生成中', desc: '等待 AI 回图', count: items.filter(item => item.status === 'generating').length },
    { value: 'active', label: '已发布', desc: '可被教程/参数表引用', count: items.filter(item => item.status === 'active').length },
    { value: 'failed', label: '异常', desc: '需要重试或修正', count: items.filter(item => item.status === 'failed').length },
  ], [filteredTotal, items]);
  const selectedImage = getVisualReferenceDetailImage(draft);
  const selectedVideo = getVisualReferenceVideo(draft);
  const selectedIsVideo = isVisualReferenceVideo(draft);

  return (
    <div className="space-y-4">
      {/* 页头：标题 + 主操作 */}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-[15px] font-semibold flex items-center gap-2" style={{ color: 'var(--color-text-primary)' }}>
            <Images size={15} style={{ color: ACCENT }} />
            视觉参考库
          </h2>
          <p className="text-xs mt-1 max-w-2xl" style={{ color: 'var(--color-text-muted)' }}>
            参考资产生产台 · 管理画风、焦段、景别、运镜等标准样张，产出进入教程页、镜头参数速查表和小故事板高级设置。
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          <SecondaryButton onClick={handleSeedDefaults} disabled={saving} className="inline-flex items-center gap-1.5">
            <Sparkles size={13} />
            初始化标准库
          </SecondaryButton>
          <PrimaryButton onClick={startNew} className="inline-flex items-center gap-1.5">
            <Plus size={13} />
            新建参考项
          </PrimaryButton>
        </div>
      </div>

      {/* 状态数据带：点击单元 = 状态筛选；gap-px hairline 分隔，选中态 accent 下划线 + 浅 tint */}
      <div className="rounded-lg border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-secondary)' }}>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px" style={{ backgroundColor: 'var(--color-border-secondary)' }}>
          {statusSteps.map((step) => {
            const active = filters.status === step.value;
            return (
              <button
                key={step.value || 'all'}
                type="button"
                onClick={() => setFilter('status', step.value)}
                className={`text-left px-4 py-3 transition-colors duration-150 cursor-pointer ${active ? '' : 'hover:[--vr-step-bg:var(--color-bg-tertiary)]'}`}
                style={{
                  backgroundColor: active ? 'color-mix(in srgb, var(--color-warning) 6%, var(--color-bg-secondary))' : 'var(--vr-step-bg, var(--color-bg-secondary))',
                  boxShadow: active ? 'inset 0 -2px 0 var(--color-warning)' : 'none',
                }}
              >
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-label">{step.label}</span>
                  <span className="font-mono-num text-lg leading-7 font-semibold" style={{ color: 'var(--color-text-primary)' }}>{step.count}</span>
                </div>
                <div className="text-2xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>{step.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {error && <ErrorNotice message={error} />}
      {notice && (
        <div className="rounded-lg px-3.5 py-2.5 text-sm animate-in fade-in slide-in-from-top-1 duration-200" style={{ backgroundColor: 'color-mix(in srgb, var(--color-success) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--color-success) 45%, transparent)', color: 'var(--color-success)' }}>
          {notice}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[220px_minmax(0,1fr)] 2xl:grid-cols-[240px_minmax(620px,1fr)_420px] gap-4 items-start">
        {/* 栏 1：用途分类侧栏 */}
        <Panel title="用途分类" sub="按使用场景维护" className="xl:sticky xl:top-4">
          <div className="space-y-1">
            <SideFilterRow
              active={!filters.category}
              label="全部用途"
              count={data?.total ?? 0}
              onClick={() => setFilter('category', '')}
            />
            {categoryCounts.map((cat) => (
              <SideFilterRow
                key={cat.value}
                active={filters.category === cat.value}
                label={cat.label}
                count={cat.count}
                onClick={() => setFilter('category', cat.value)}
              />
            ))}
          </div>
          <div className="pt-3 mt-3 border-t" style={{ borderColor: 'var(--color-border-secondary)' }}>
            <div className="text-label mb-2">项目域</div>
            <div className="space-y-1">
              {['', 'global', 'domestic', 'overseas'].map(value => (
                <SideFilterRow
                  key={value || 'all-market'}
                  compact
                  active={filters.market === value}
                  label={value ? marketLabel(value) : '全部'}
                  onClick={() => setFilter('market', value)}
                />
              ))}
            </div>
          </div>
        </Panel>

        {/* 栏 2：参考卡片墙 */}
        <Panel
          title="参考卡片"
          sub="点击卡片进行审核与生成"
          className="min-w-0"
          right={(
            <div className="flex items-center gap-2">
              <span className="text-label"><span className="font-mono-num">{items.length}</span> 项</span>
              <button
                type="button"
                onClick={refreshVisualReferences}
                className="w-8 h-8 rounded-md border flex items-center justify-center cursor-pointer transition-colors duration-150 hover:[--vr-refresh-bg:var(--color-bg-tertiary)]"
                style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--vr-refresh-bg, var(--color-bg-secondary))' }}
                title="刷新"
              >
                <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
              </button>
            </div>
          )}
        >
          <div className="flex flex-wrap items-center gap-2.5 mb-3">
            <div className="flex-1 min-w-[240px] h-8 flex items-center gap-2 px-2.5 rounded-md" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)' }}>
              <Search size={13} style={{ color: 'var(--color-text-muted)' }} />
              <input
                value={filters.q}
                onChange={(event) => setFilter('q', event.target.value)}
                placeholder="搜索参考项、提示词、标签"
                className="flex-1 bg-transparent outline-none text-xs"
                style={{ color: 'var(--color-text-primary)' }}
              />
            </div>
            <AppSelect
              value={filters.mediaType}
              onChange={(event) => setFilter('mediaType', event.target.value)}
              className="h-8 px-2 rounded-md text-xs outline-none"
              style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-primary)', color: 'var(--color-text-primary)' }}
            >
              <option value="">全部媒体</option>
              <option value="image">图片</option>
              <option value="video">视频</option>
            </AppSelect>
          </div>

          {!items.length && loading ? (
            <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}>
              {Array.from({ length: 8 }).map((_, i) => (
                <div key={i} className="rounded-lg border overflow-hidden" style={{ backgroundColor: 'var(--color-bg-secondary)', borderColor: 'var(--color-border-secondary)' }}>
                  <div className="skeleton aspect-[4/3]" style={{ borderRadius: 0 }} />
                  <div className="p-3 space-y-2">
                    <div className="skeleton h-3 w-2/3" />
                    <div className="skeleton h-3 w-1/2" />
                  </div>
                </div>
              ))}
            </div>
          ) : !items.length ? (
            <EmptyBlock text="暂无参考项" hint="先初始化标准库或新建一个。" />
          ) : (
            <div
              className="grid gap-3 max-h-[760px] overflow-y-auto custom-scrollbar"
              style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(190px, 1fr))' }}
            >
              {items.map(item => {
                const active = activeId && String(item.id) === String(activeId);
                return (
                  <VisualReferenceCard
                    key={item.id}
                    item={item}
                    active={!!active}
                    categoryLabelText={categoryLabel(item.category)}
                    statusLabelText={statusLabel(item.status)}
                    marketLabelText={marketLabel(item.market)}
                    onSelect={handleSelectVisualReference}
                  />
                );
              })}
            </div>
          )}
        </Panel>

        {/* 栏 3：审核与生成 */}
        <Panel
          title={(
            <span className="inline-flex items-center gap-1.5">
              <Eye size={13} style={{ color: ACCENT }} />
              {draft.id ? '审核与生成' : '新建参考项'}
            </span>
          )}
          sub={draft.id ? (<><span className="font-mono-num">#{draft.id}</span> · {statusLabel(draft.status)}</>) : '先填写基础信息并保存'}
          className="xl:col-span-2 2xl:col-span-1 2xl:sticky 2xl:top-4"
          right={(
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-md text-2xs font-medium shrink-0"
              style={{ color: ACCENT, backgroundColor: 'color-mix(in srgb, var(--color-warning) 12%, transparent)' }}
            >
              {categoryLabel(draft.category)}
            </span>
          )}
        >
          <div className="max-h-[820px] overflow-y-auto custom-scrollbar -mr-1 pr-1">
            <div className="h-52 rounded-md flex items-center justify-center overflow-hidden" style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border-secondary)' }}>
              {selectedIsVideo && selectedVideo ? (
                <video
                  key={selectedVideo}
                  src={selectedVideo}
                  className="w-full h-full object-contain"
                  controls
                  playsInline
                  preload="metadata"
                />
              ) : selectedImage ? (
                <img
                  key={selectedImage}
                  src={selectedImage}
                  alt={draft.name || '参考图'}
                  className="w-full h-full object-contain"
                  decoding="async"
                  onLoad={(event) => visualReferencePerfLog('detail-image:load', {
                    id: draft.id,
                    urlLength: selectedImage.length,
                    naturalWidth: event.currentTarget.naturalWidth,
                    naturalHeight: event.currentTarget.naturalHeight,
                  })}
                  onError={() => visualReferencePerfLog('detail-image:error', {
                    id: draft.id,
                    urlLength: selectedImage.length,
                  })}
                />
              ) : draft.status === 'generating' ? (
                <div className="w-full h-full relative">
                  <div className="skeleton absolute inset-0" style={{ borderRadius: 0 }} />
                  <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                    <Sparkles size={16} style={{ color: ACCENT }} />
                    生成中，完成后自动刷新
                  </div>
                </div>
              ) : (
                <div className="text-2xs flex flex-col items-center gap-1.5" style={{ color: 'var(--color-text-muted)' }}>
                  <Images size={18} style={{ opacity: 0.55 }} />
                  {selectedIsVideo ? '还没有参考视频' : '还没有参考图'}
                </div>
              )}
            </div>

            <div className="flex flex-wrap gap-2 mt-3">
              <SecondaryButton onClick={handleSave} disabled={saving} className="flex-1 min-w-[96px] inline-flex items-center justify-center gap-1.5">
                <Save size={13} />
                {saving ? '保存中' : '保存'}
              </SecondaryButton>
              <PrimaryButton
                onClick={handleGenerate}
                disabled={generating || !draft.id}
                className="flex-1 min-w-[118px] inline-flex items-center justify-center gap-1.5"
              >
                <Sparkles size={13} />
                {generating ? '提交中' : '生成'}
              </PrimaryButton>
              <button
                type="button"
                onClick={handleSync}
                disabled={!draft.id}
                className="w-8 h-8 inline-flex items-center justify-center rounded-md border cursor-pointer disabled:opacity-50 transition-colors duration-150 hover:[--vr-sync-bg:var(--color-bg-secondary)]"
                style={{ borderColor: 'var(--color-border-primary)', color: 'var(--color-text-secondary)', backgroundColor: 'var(--vr-sync-bg, var(--color-bg-tertiary))' }}
                title="同步任务状态"
              >
                <RefreshCw size={14} />
              </button>
            </div>

            <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: 'var(--color-border-secondary)' }}>
              <div className="text-label">基础信息</div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="分类" value={draft.category} onChange={(value) => setField('category', value)}>
                  {VISUAL_REFERENCE_CATEGORIES.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
                </SelectField>
                <SelectField label="项目域" value={draft.market} onChange={(value) => setField('market', value)}>
                  <option value="global">通用</option>
                  <option value="domestic">国内</option>
                  <option value="overseas">出海</option>
                </SelectField>
              </div>
              <InputField label="名称" value={draft.name} onChange={(value) => setField('name', value)} placeholder="例如：35mm 人文焦段" />
              <InputField label="Key" value={draft.itemKey} onChange={(value) => setField('itemKey', value)} placeholder="lens_35mm" />
              <TextAreaField label="管理摘要" value={draft.summary} onChange={(value) => setField('summary', value)} rows={3} placeholder="这张参考图要让使用者理解什么。" />
            </div>

            <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: 'var(--color-border-secondary)' }}>
              <div className="text-label">生成参数</div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="比例" value={normalizeDatastartImageAspectRatio(draft.aspectRatio, effectiveImageProvider)} onChange={(value) => setField('aspectRatio', value)}>
                  {getCompatibleImageAspectOptions(['16:9', '9:16', '1:1', '4:3', '3:4'].map(value => ({ value, label: value })), effectiveImageProvider)
                    .map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                </SelectField>
                <SelectField label="档位" value={normalizeWanxImageQualityTier(draft.imageQualityTier, effectiveImageProvider)} onChange={(value) => setField('imageQualityTier', value)}>
                  {availableImageQualityOptions.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </SelectField>
              </div>
              <SelectField label="模型" value={effectiveImageProvider} onChange={(value) => setDraft(prev => ({
                ...prev,
                provider: value,
                imageQualityTier: normalizeWanxImageQualityTier(prev.imageQualityTier, value),
              }))}>
                {VISUAL_REFERENCE_PROVIDERS.map(item => <option key={item.value} value={item.value}>{item.label}</option>)}
              </SelectField>
            </div>

            <div className="mt-4 space-y-3">
              <TextAreaField label="生成提示词" value={draft.prompt} onChange={(value) => setField('prompt', value)} rows={7} placeholder="用于生成参考图的完整提示词。" />
              <TextAreaField label="负面提示词" value={draft.negativePrompt} onChange={(value) => setField('negativePrompt', value)} rows={3} />
            </div>

            <div className="mt-4 pt-4 border-t space-y-3" style={{ borderColor: 'var(--color-border-secondary)' }}>
              <div className="text-label">发布信息</div>
              <div className="grid grid-cols-2 gap-3">
                <SelectField label="状态" value={draft.status} onChange={(value) => setField('status', value)}>
                  <option value="draft">草稿</option>
                  <option value="active">可用</option>
                  <option value="generating">生成中</option>
                  <option value="failed">失败</option>
                </SelectField>
                <InputField label="排序" value={draft.sortOrder ?? ''} onChange={(value) => setField('sortOrder', value)} placeholder="100" />
              </div>
              <InputField label="标签" value={draft.tags} onChange={(value) => setField('tags', value)} placeholder="画风,焦段,教程" />
              <InputField label="参考图 URL" value={draft.referenceUrl} onChange={(value) => setField('referenceUrl', value)} />
              <InputField label="参考视频 URL" value={draft.previewVideoUrl} onChange={(value) => setField('previewVideoUrl', value)} />
            </div>

            <div className="flex justify-between items-center pt-3 mt-4 border-t" style={{ borderColor: 'var(--color-border-secondary)' }}>
              <div className="text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                任务：<span className="font-mono-num">{draft.taskId || '-'}</span>
              </div>
              <DangerButton onClick={handleDelete} disabled={!draft.id || saving} className="inline-flex items-center gap-1">
                <Trash2 size={12} />
                移入回收站
              </DangerButton>
            </div>
          </div>
        </Panel>
      </div>
    </div>
  );
};

export default VisualReferenceAdminTab;
